import {
  FileKindSnapshot,
  PdfThumbnailFileSnapshot,
  PdfThumbnailModel,
} from './types';
import { ImageConvertStatus, isImageConvertStatusSuccess } from 'shared/domain/imageConvertStatus/imageConvertStatus';
import {
  isImageExtension,
  resolveMimeTypeFromExtension,
} from 'shared/utils/fileExtensions';
import {
  isDisplayableNonPdfSource,
  isImagePreviewUrl,
  isPersistedSource,
  isPdfSignedSource,
  isUsableSource,
} from 'shared/utils/sourceUrl';
import { isBlobUrl } from 'shared/utils/blobUrl';
import { MIME_TYPE_GROUPS, MIME_TYPES } from 'shared/constants/MIME_TYPES';
import { EXTENSIONS } from 'shared/constants/EXTENSIONS';

export class FileKindService {
  private normalizeImageConvertStatus(
    rawStatus: unknown
  ): ImageConvertStatus | undefined {
    if (typeof rawStatus !== 'string') {
      return undefined;
    }

    const normalized = rawStatus
      .trim()
      .toUpperCase()
      .replace(/[\s-]+/g, '_');

    switch (normalized) {
      case ImageConvertStatus.PENDING:
        return ImageConvertStatus.PENDING;
      case ImageConvertStatus.INCORRECT_PAGES:
        return ImageConvertStatus.INCORRECT_PAGES;
      case ImageConvertStatus.FAILED:
        return ImageConvertStatus.FAILED;
      case ImageConvertStatus.SUCCESS:
        return ImageConvertStatus.SUCCESS;
      case ImageConvertStatus.UNCONVERTABLE_EXTENSION:
        return ImageConvertStatus.UNCONVERTABLE_EXTENSION;
      default:
        return undefined;
    }
  }

  hasConvertedPdfPreviewSource(file: FileKindSnapshot): boolean {
    return Boolean(
      file.signedRequest?.imageSignedRequest ||
        file.signedRequest?.mergedSignedRequest ||
        file.signedRequest?.thumbnailSignedRequest ||
        (typeof file.thumbnail === 'string' &&
          file.thumbnail.includes(`.${EXTENSIONS.WEBP}`)) ||
        (isPersistedSource(file.src) && !isPdfSignedSource(file.src))
    );
  }

  isPendingConversionPdf(file: FileKindSnapshot): boolean {
    const isSyncedToServer = Boolean(file._id || file.documentationId);
    if (!this.isPdf(file) || !isSyncedToServer) {
      return false;
    }

    if (
      file.signedRequest?.imageSignedRequest ||
      file.signedRequest?.mergedSignedRequest
    ) {
      return false;
    }

    if (typeof file.pdfPageCount === 'number') {
      return file.pdfPageCount === 1;
    }

    const normalizedStatus = this.normalizeImageConvertStatus(
      file.imageConvertStatus as unknown
    );
    if (
      normalizedStatus === ImageConvertStatus.INCORRECT_PAGES ||
      normalizedStatus === ImageConvertStatus.UNCONVERTABLE_EXTENSION ||
      normalizedStatus === undefined
    ) {
      return false;
    }

    return true;
  }

  isVideo(file: FileKindSnapshot): boolean {
    return (
      Boolean(file.type?.includes('video')) ||
      this.hasAnyExtension(file, EXTENSIONS.VIDEO)
    );
  }

  resolveVideoMimeType(file: FileKindSnapshot): string | undefined {
    if (typeof file.type === 'string' && file.type.includes('video/')) {
      return file.type;
    }

    const extension = file.data?.extension;
    if (!extension) {
      return undefined;
    }

    const mimeType = resolveMimeTypeFromExtension(extension, '');
    return mimeType.startsWith('video/') ? mimeType : undefined;
  }

  isPdf(file: FileKindSnapshot): boolean {
    return (
      Boolean(file.type?.includes(MIME_TYPES.PDF)) ||
      Boolean(file.type?.includes(EXTENSIONS.PDF)) ||
      this.hasExtension(file, EXTENSIONS.PDF)
    );
  }

  isImage(file: FileKindSnapshot): boolean {
    const normalizedType = file.type?.trim().toLowerCase();

    if (normalizedType?.includes('image')) {
      return true;
    }

    if (file.data?.extension && isImageExtension(file.data.extension)) {
      return true;
    }

    // Some payloads return extension in `type` (e.g. "jpg") instead of MIME.
    if (!normalizedType) {
      return false;
    }

    const slashIndex = normalizedType.lastIndexOf('/');
    const typeAsExtension =
      slashIndex >= 0
        ? normalizedType.slice(slashIndex + 1)
        : normalizedType;

    return isImageExtension(typeAsExtension.replace(/^\./, ''));
  }

  isConvertedPdf(file: FileKindSnapshot): boolean {
    return (
      this.isPdf(file) &&
      (this.hasConvertedPdfPreviewSource(file) ||
        isImageConvertStatusSuccess(file.imageConvertStatus) ||
        Boolean(file.signedRequest?.mergedSignedRequest))
    );
  }

  isDrawablePdf(file: FileKindSnapshot): boolean {
    return (
      this.hasExtension(file, EXTENSIONS.PDF) &&
      (isImageConvertStatusSuccess(file.imageConvertStatus) ||
        Boolean(file.signedRequest?.imageSignedRequest))
    );
  }

  isFileImage(file: FileKindSnapshot): boolean {
    return this.isImage(file) || this.isDrawablePdf(file);
  }

  isDrawableExtension(file: FileKindSnapshot): boolean {
    if (this.isDrawablePdf(file)) {
      return true;
    }

    return Boolean(
      file.data?.extension && isImageExtension(file.data.extension)
    );
  }

  isWord(file: FileKindSnapshot): boolean {
    return (
      this.hasAnyMimeType(file.type, MIME_TYPES.WORD) ||
      this.hasAnyExtension(file, EXTENSIONS.WORD)
    );
  }

  isExcel(file: FileKindSnapshot): boolean {
    return (
      this.hasAnyMimeType(file.type, MIME_TYPES.EXCEL) ||
      this.hasAnyExtension(file, EXTENSIONS.EXCEL)
    );
  }

  isWordContentType(contentType: string | null | undefined): boolean {
    return this.hasAnyMimeType(contentType, MIME_TYPE_GROUPS.WORD_WITH_FALLBACK);
  }

  isExcelContentType(contentType: string | null | undefined): boolean {
    return this.hasAnyMimeType(contentType, MIME_TYPE_GROUPS.EXCEL_WITH_FALLBACK);
  }

  shouldShowAsImageInGallery(file: FileKindSnapshot): boolean {
    return (
      this.isImage(file) ||
      (this.isConvertedPdf(file) &&
        this.hasConvertedPdfPreviewSource(file))
    );
  }

  resolvePdfPreviewSource(file: FileKindSnapshot): string | undefined {
    if (isPersistedSource(file.signedRequest?.signedRequest)) {
      return file.signedRequest.signedRequest;
    }

    // Drawing merge stores a raster blob in downloadSrc — never feed it to pdf.js.
    if (isPersistedSource(file.downloadSrc)) {
      return file.downloadSrc;
    }

    return undefined;
  }

  resolvePdfThumbnailImageSource(file: FileKindSnapshot): string | undefined {
    if (
      isDisplayableNonPdfSource(file.signedRequest?.thumbnailSignedRequest)
    ) {
      return file.signedRequest.thumbnailSignedRequest;
    }

    if (isDisplayableNonPdfSource(file.signedRequest?.imageSignedRequest)) {
      return file.signedRequest.imageSignedRequest;
    }

    if (isImagePreviewUrl(file.thumbnail)) {
      return file.thumbnail;
    }

    if (this.isPdf(file) && isImagePreviewUrl(file.src)) {
      return file.src;
    }

    return undefined;
  }

  resolvePdfClientRenderSource(file: FileKindSnapshot): string | undefined {
    if (
      isPersistedSource(file.signedRequest?.signedRequest) &&
      isPdfSignedSource(file.signedRequest.signedRequest)
    ) {
      return file.signedRequest.signedRequest;
    }

    if (isBlobUrl(file.downloadSrc)) {
      return file.downloadSrc;
    }

    if (
      isPersistedSource(file.downloadSrc) &&
      isPdfSignedSource(file.downloadSrc)
    ) {
      return file.downloadSrc;
    }

    if (isUsableSource(file.downloadSrc) && this.isPdf(file)) {
      return file.downloadSrc;
    }

    return undefined;
  }

  resolvePdfThumbnailSource(file: FileKindSnapshot): string | undefined {
    return (
      this.resolvePdfThumbnailImageSource(file) ??
      this.resolvePdfClientRenderSource(file)
    );
  }

  buildPdfThumbnailCacheKey(
    file: Pick<PdfThumbnailFileSnapshot, '_id' | 'localId'>
  ): string | undefined {
    if (typeof file._id === 'string' && file._id.length > 0) {
      return `doc:${file._id}`;
    }

    if (typeof file.localId === 'number') {
      return `local:${file.localId}`;
    }

    return undefined;
  }

  /**
   * Single entry point for PDF miniatures in list/gallery.
   * Prefers server image preview, then client pdf.js render source.
   */
  resolvePdfThumbnailModel(
    file: PdfThumbnailFileSnapshot
  ): PdfThumbnailModel | null {
    if (!this.isPdf(file)) {
      return null;
    }

    const src = this.resolvePdfThumbnailSource(file);

    if (src) {
      return {
        kind: 'ready',
        src,
        cacheKey: this.buildPdfThumbnailCacheKey(file),
      };
    }

    return { kind: 'loading' };
  }

  resolveVideoPreviewSource(file: FileKindSnapshot): string | undefined {
    if (isUsableSource(file.downloadSrc)) {
      return file.downloadSrc;
    }

    if (isPersistedSource(file.signedRequest?.signedRequest)) {
      return file.signedRequest.signedRequest;
    }

    if (isPersistedSource(file.signedRequest?.mergedSignedRequest)) {
      return file.signedRequest.mergedSignedRequest;
    }

    if (isUsableSource(file.thumbnail)) {
      return file.thumbnail;
    }

    return undefined;
  }

  private hasExtension(file: FileKindSnapshot, extension: string): boolean {
    return file.data?.extension?.toLowerCase() === extension;
  }

  private hasAnyExtension(
    file: FileKindSnapshot,
    extensions: readonly string[]
  ): boolean {
    const extension = file.data?.extension?.toLowerCase();
    return Boolean(extension) && extensions.some((item) => item === extension);
  }

  private hasAnyMimeType(
    contentType: string | null | undefined,
    mimeTypes: readonly string[]
  ): boolean {
    if (!contentType) {
      return false;
    }

    return mimeTypes.some((mimeType) => contentType.includes(mimeType));
  }

  /**
   * Checks whether file has any usable source URL for preview (blob URL or signed URL).
   */
  hasUsablePreviewSource(file: FileKindSnapshot): boolean {
    return (
      isUsableSource(file.thumbnail) ||
      isUsableSource(file.downloadSrc) ||
      isUsableSource(file.signedRequest?.imageSignedRequest) ||
      isUsableSource(file.signedRequest?.signedRequest)
    );
  }
}

export const fileKindService = new FileKindService();

export type {
  FileKindSnapshot,
  PdfThumbnailFileSnapshot,
  PdfThumbnailModel,
} from './types';
